/**
 * Procedural map generation.
 *
 * Two jobs, in order: paint biomes, then choose where kingdoms start. The second
 * is the one that decides whether a session is worth playing — a player who
 * opens on a mountain shelf with no food reads the game as broken rather than
 * hard, and never comes back to find out which it was.
 *
 * Everything here is a pure function of `options.seed` via `createRng`. No
 * `Math.random`, no `Date.now`. The same seed must produce a byte-identical map
 * forever, because that is what lets a balance report be reproduced and what the
 * deterministic-simulation tests depend on.
 *
 * A note on the `?? fallback` reads below: the project compiles with
 * `noUncheckedIndexedAccess`, so every array index is `T | undefined`. Where an
 * index is provably in range the fallback is unreachable; it is there to satisfy
 * the compiler without an assertion, not because the value can be missing.
 */
import { BIOMES, BIOME_YIELDS, DEFAULT_KINGDOM_COUNT, DEFAULT_MAP_HEIGHT, DEFAULT_MAP_WIDTH, RESOURCE_TYPES, SPECIAL_HEXES_PER_1600_HEXES, TERRAIN_RESOURCE_TYPES, SPECIAL_HEX_MIN_SEPARATION, SPECIAL_HEX_MIN_SPAWN_DISTANCE, STARTING_CLAIM_RADIUS, addBundles, emptyBundle, hexDistance, hexKey, hexSpiral, hexToPixel, hexYield, neighbors, rectangularMapCoords, } from "kngdm-shared";
import { createRng, pick, randomInt } from "./rng.js";
import { createHexRecord } from "./state.js";
// ---------------------------------------------------------------------------
// Tunables
//
// These belong in `shared/src/balance.ts` alongside every other tunable number,
// and should move there the moment that file grows a map-generation section.
// They live here for now only because balance.ts has no such section yet and is
// owned by another module; nothing outside this file reads them.
// ---------------------------------------------------------------------------
/** Octaves of value noise per field. Four is where added detail stops reading as terrain. */
const NOISE_OCTAVES = 4;
/**
 * Lattice cells across the map at the lowest octave — i.e. continent scale.
 *
 * Five, not three. Three gives handsomer, larger landmasses, but at that scale a
 * single elevation feature can cover an entire polar region and wipe the tundra
 * band off the map; measured over 2000 seeds it dropped tundra as low as 3.3% of
 * a 40x40 map. Five keeps every biome above the 5% floor.
 */
const NOISE_BASE_CELLS = 5;
/** Quantile thresholds on the elevation field. See `classifyBiomes`. */
const MOUNTAIN_ELEVATION = 0.72;
const HILL_ELEVATION = 0.55;
/**
 * Distance from the equator, as a fraction of map height, at which land turns to
 * tundra.
 *
 * 0.38 rather than the 0.42 this started at. Tundra is the one biome whose share
 * isn't pinned by a quantile — it gets whatever the poles have left after
 * mountains and hills take their cut — so it needs the widest band to clear the
 * 5% floor on its unluckiest seeds. At 0.38 the measured range over 2000 seeds
 * is 5.3%-23.2%; at 0.42 it was 3.3%-19.4%.
 */
const TUNDRA_LATITUDE_BAND = 0.38;
/** Quantile thresholds on the moisture field, applied to land below hill height. */
const FOREST_MOISTURE = 0.58;
const PLAINS_MOISTURE = 0.32;
/**
 * Fraction of a "fair share" radius that spawns must be apart.
 *
 * A kingdom's fair share of a map is `hexCount / kingdomCount`, which inverts to
 * a hex radius R. Two spawns exactly 2R apart would mean perfectly tiled, non-
 * overlapping empires — unreachable on a finite rectangle, so we ask for a
 * fraction of it and let the relaxation ladder in `placeSpawns` back off if even
 * that is impossible on a particular seed.
 */
const SPAWN_SEPARATION_FACTOR = 1.35;
/**
 * Floor on the *derived* separation, so a tiny map doesn't ask for a spacing of
 * one and pile every kingdom into the same valley.
 *
 * This is a target, not a guarantee: `placeSpawns` relaxes below it rather than
 * failing to produce a map at all, which it has to do on maps too cramped to
 * hold `kingdomCount` spawns this far apart.
 */
export const MIN_IDEAL_SPAWN_SEPARATION = 3;
/**
 * How much a candidate's *worst* resource counts relative to its average.
 *
 * Raising this pushes spawns toward mixed terrain. It matters more than the
 * total: an opening where one of the four resources is flatly unavailable locks
 * a player out of expanding in any direction (every expansion cost in
 * balance.ts draws on at least three resources), while a merely low total just
 * means a slower start.
 */
const SPAWN_SCARCITY_WEIGHT = 1.5;
/** Standard options for a default session, for callers that only have a seed. */
export function defaultMapGenOptions(seed) {
    return {
        seed,
        width: DEFAULT_MAP_WIDTH,
        height: DEFAULT_MAP_HEIGHT,
        kingdomCount: DEFAULT_KINGDOM_COUNT,
    };
}
// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
export function generateMap(options) {
    const { seed, width, height, kingdomCount } = options;
    if (!Number.isInteger(width) || width <= 0 || !Number.isInteger(height) || height <= 0) {
        throw new RangeError(`Map size must be positive integers, got ${width}x${height}`);
    }
    if (!Number.isInteger(kingdomCount) || kingdomCount <= 0) {
        throw new RangeError(`kingdomCount must be a positive integer, got ${kingdomCount}`);
    }
    const coords = rectangularMapCoords(width, height);
    const rng = createRng(seed);
    // Two independent fields drawn from one stream. Sampling elevation fully
    // before moisture (rather than interleaving) keeps each field's noise
    // reproducible even if the other's octave count is later retuned.
    const elevation = quantiles(sampleField(rng, coords));
    const moisture = sampleField(rng, coords);
    const biomes = classifyBiomes(coords, elevation, moisture, height);
    const hexes = new Map();
    for (const [index, coord] of coords.entries()) {
        const record = createHexRecord(coord, biomes[index] ?? "plains");
        hexes.set(record.key, record);
    }
    const spawns = placeSpawns(hexes, coords, kingdomCount);
    // Specials are placed after spawns because their whole placement rule is
    // "far from every spawn". The chosen records are rebuilt rather than mutated,
    // which keeps `special` readonly on HexRecord — safe here and nowhere else,
    // because generation is the one moment nothing holds a reference to a hex yet.
    for (const [key, special] of placeSpecialHexes(hexes, coords, spawns, rng)) {
        const existing = hexes.get(key);
        /* c8 ignore next */
        if (existing === undefined)
            continue;
        hexes.set(key, createHexRecord(existing.coord, existing.biome, special));
    }
    return { hexes, spawns };
}
/**
 * Scatter the Aether-bearing special hexes.
 *
 * Two constraints, both from docs/07 Stage 5.1's "rare and contested-by-design":
 * every special sits at least `SPECIAL_HEX_MIN_SPAWN_DISTANCE` from every spawn,
 * so nobody starts on top of one, and at least `SPECIAL_HEX_MIN_SEPARATION` from
 * every other, so they don't clump into a single winner-takes-all corner.
 *
 * Constraints are relaxed a hex at a time if the map is too cramped to satisfy
 * them, exactly as `placeSpawns` does: a map that generates with tighter spacing
 * is far better than one that refuses to generate. On a map so small that even
 * the relaxed pass finds nowhere, the result is simply fewer specials — Aether
 * becoming unavailable is a survivable outcome, a failed generation is not.
 */
function placeSpecialHexes(hexes, coords, spawns, rng) {
    const target = Math.max(1, Math.round((SPECIAL_HEXES_PER_1600_HEXES * coords.length) / 1600));
    // Deterministic candidate order: sorted by key, then shuffled by the seeded
    // rng. Iterating `coords` directly would bias every map toward the top-left.
    const shuffled = [...coords].sort((a, b) => hexKey(a).localeCompare(hexKey(b)));
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = randomInt(rng, 0, i + 1);
        const a = shuffled[i];
        const b = shuffled[j];
        /* c8 ignore next */
        if (a === undefined || b === undefined)
            continue;
        shuffled[i] = b;
        shuffled[j] = a;
    }
    for (let spawnDistance = SPECIAL_HEX_MIN_SPAWN_DISTANCE, separation = SPECIAL_HEX_MIN_SEPARATION; spawnDistance >= 1 || separation >= 1; spawnDistance = Math.max(1, spawnDistance - 1), separation = Math.max(1, separation - 1)) {
        const chosen = greedyPlaceSpecials(hexes, shuffled, spawns, target, spawnDistance, separation, rng);
        if (chosen.size >= target)
            return chosen;
        // Both floors reached and still short: take the best effort rather than loop.
        if (spawnDistance === 1 && separation === 1)
            return chosen;
    }
    /* c8 ignore next */
    return new Map();
}
function greedyPlaceSpecials(hexes, candidates, spawns, target, minSpawnDistance, minSeparation, rng) {
    const chosen = new Map();
    const placed = [];
    for (const coord of candidates) {
        if (chosen.size >= target)
            break;
        const record = hexes.get(hexKey(coord));
        if (record === undefined)
            continue;
        if (spawns.some((spawn) => hexDistance(spawn, coord) < minSpawnDistance))
            continue;
        if (placed.some((other) => hexDistance(other, coord) < minSeparation))
            continue;
        chosen.set(record.key, specialForBiome(record.biome, rng));
        placed.push(coord);
    }
    return chosen;
}
/**
 * Which special a biome grows.
 *
 * Flavour rather than mechanics — the yields differ by type, so tying type to
 * terrain means the mountains are where you go for ore-and-Aether and the ruins
 * are out on the open ground. A weighted draw rather than a fixed mapping so a
 * biome does not become a perfect predictor.
 */
function specialForBiome(biome, rng) {
    const table = {
        mountains: ["crystalVein", "crystalVein", "geyser"],
        hills: ["crystalVein", "crystalVein", "ruins"],
        tundra: ["geyser", "geyser", "crystalVein"],
        desert: ["geyser", "ruins", "ruins"],
        plains: ["ruins", "ruins", "geyser"],
        forest: ["ruins", "ruins", "crystalVein"],
    };
    return pick(rng, table[biome]);
}
// ---------------------------------------------------------------------------
// Biome classification
// ---------------------------------------------------------------------------
/**
 * Elevation wins over latitude, and latitude over moisture.
 *
 * The ordering is the whole model: height is absolute (a peak is a peak at any
 * latitude), the poles are cold whatever falls on them, and only the temperate
 * lowland left over is decided by rainfall. Inverting any pair of these produces
 * maps that look procedurally generated instead of geographic.
 *
 * It runs in two passes because the moisture thresholds are ranked *within the
 * temperate lowland* rather than across the whole map. Ranking globally would
 * mean a seed whose lowland happens to sit under a wet part of the moisture
 * field gets almost no desert — measured as low as 2.7% — since the hexes that
 * would have been dry all turned out to be mountains. Conditioning on the set
 * the thresholds actually apply to is what makes "the driest third of the
 * lowland is desert" true rather than aspirational.
 */
function classifyBiomes(coords, elevation, moisture, height) {
    const biomes = new Array(coords.length);
    const lowland = [];
    for (const [index, coord] of coords.entries()) {
        const altitude = elevation[index] ?? 0.5;
        if (altitude > MOUNTAIN_ELEVATION) {
            biomes[index] = "mountains";
        }
        else if (altitude > HILL_ELEVATION) {
            biomes[index] = "hills";
        }
        else if (Math.abs(latitudeOf(coord.r, height) - 0.5) > TUNDRA_LATITUDE_BAND) {
            biomes[index] = "tundra";
        }
        else {
            lowland.push(index);
        }
    }
    const lowlandMoisture = quantiles(lowland.map((index) => moisture[index] ?? 0.5));
    for (const [position, index] of lowland.entries()) {
        const wetness = lowlandMoisture[position] ?? 0.5;
        biomes[index] =
            wetness > FOREST_MOISTURE ? "forest" : wetness > PLAINS_MOISTURE ? "plains" : "desert";
    }
    return biomes;
}
/** Row index as a 0..1 latitude. A one-row map is all equator. */
function latitudeOf(row, height) {
    if (height <= 1)
        return 0.5;
    return row / (height - 1);
}
// ---------------------------------------------------------------------------
// Fractal value noise
// ---------------------------------------------------------------------------
/**
 * Sample one fractal field at every hex, in `coords` order.
 *
 * Sampling at `hexToPixel` position rather than at (q, r) matters: axial
 * coordinates are sheared relative to the screen, so noise laid out in (q, r)
 * produces features that visibly lean. Pixel space is the space the player sees
 * the terrain in, so it is the space the terrain should be shaped in.
 */
function sampleField(rng, coords) {
    const octaves = [];
    let cells = NOISE_BASE_CELLS;
    for (let octave = 0; octave < NOISE_OCTAVES; octave++) {
        octaves.push({ cells, lattice: makeLattice(rng, cells) });
        cells *= 2; // double frequency, and half amplitude below
    }
    // Normalise pixel extent to 0..1 so the same octave count gives the same
    // feature scale on a 20x20 map as on a 120x60 one.
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    const points = coords.map((coord) => {
        const point = hexToPixel(coord, 1);
        if (point.x < minX)
            minX = point.x;
        if (point.x > maxX)
            maxX = point.x;
        if (point.y < minY)
            minY = point.y;
        if (point.y > maxY)
            maxY = point.y;
        return point;
    });
    const spanX = maxX - minX || 1;
    const spanY = maxY - minY || 1;
    return points.map((point) => {
        const u = (point.x - minX) / spanX;
        const v = (point.y - minY) / spanY;
        let total = 0;
        let amplitude = 1;
        let amplitudeSum = 0;
        for (const octave of octaves) {
            total += sampleLattice(octave.lattice, octave.cells, u, v) * amplitude;
            amplitudeSum += amplitude;
            amplitude *= 0.5;
        }
        return total / amplitudeSum;
    });
}
function makeLattice(rng, cells) {
    const stride = cells + 1;
    const lattice = new Float64Array(stride * stride);
    for (let i = 0; i < lattice.length; i++)
        lattice[i] = rng();
    return lattice;
}
/** Bilinear sample of a lattice with smoothstep easing, `u`/`v` in 0..1. */
function sampleLattice(lattice, cells, u, v) {
    const stride = cells + 1;
    const fx = clamp01(u) * cells;
    const fy = clamp01(v) * cells;
    const x0 = Math.min(Math.floor(fx), cells - 1);
    const y0 = Math.min(Math.floor(fy), cells - 1);
    const tx = smoothstep(fx - x0);
    const ty = smoothstep(fy - y0);
    const top = lerp(cell(lattice, y0 * stride + x0), cell(lattice, y0 * stride + x0 + 1), tx);
    const bottom = lerp(cell(lattice, (y0 + 1) * stride + x0), cell(lattice, (y0 + 1) * stride + x0 + 1), tx);
    return lerp(top, bottom, ty);
}
function cell(lattice, index) {
    return lattice[index] ?? 0;
}
/** Cubic ease. Linear interpolation alone leaves visible lattice creases. */
function smoothstep(t) {
    return t * t * (3 - 2 * t);
}
function lerp(a, b, t) {
    return a + (b - a) * t;
}
function clamp01(value) {
    return Math.min(1, Math.max(0, value));
}
/**
 * Replace each value with its quantile in 0..1 (histogram equalisation).
 *
 * Raw summed-octave value noise is bell-shaped and its spread varies seed to
 * seed, so fixed thresholds against it give a map that is 4% mountains on one
 * seed and 30% on the next. Ranking first makes the thresholds mean exactly what
 * they read as — "the top 28% of land is mountains" — on every seed, which is
 * the only version of this that can be asserted in a test. The cost is that a
 * genuinely flat seed still gets its share of peaks; that is a fair trade for
 * never shipping a map with no ore on it.
 */
function quantiles(values) {
    const count = values.length;
    const order = values.map((_, index) => index);
    order.sort((a, b) => {
        const va = values[a] ?? 0;
        const vb = values[b] ?? 0;
        // Index tie-break, so the result never depends on the sort's stability.
        return va === vb ? a - b : va - vb;
    });
    const result = new Array(count);
    for (let rank = 0; rank < count; rank++) {
        const index = order[rank] ?? 0;
        result[index] = (rank + 0.5) / count;
    }
    return result;
}
// ---------------------------------------------------------------------------
// Spawn placement
// ---------------------------------------------------------------------------
/** Best possible per-hex yield of each resource, over all biomes. Used to normalise scores. */
const BEST_YIELD_PER_HEX = (() => {
    const best = emptyBundle();
    for (const biome of BIOMES) {
        for (const type of RESOURCE_TYPES) {
            best[type] = Math.max(best[type], BIOME_YIELDS[biome][type]);
        }
    }
    return best;
})();
/**
 * The separation two spawns must respect, derived from how much map each
 * kingdom can expect to own rather than from a hardcoded number — otherwise the
 * same constant is far too tight on a 100x100 duel and impossible on a 20x20
 * eight-way.
 */
export function idealSpawnSeparation(hexCount, kingdomCount) {
    if (kingdomCount <= 1)
        return 0;
    const share = hexCount / kingdomCount;
    // A hex disk of radius R holds 3R^2 + 3R + 1 hexes; invert that for R.
    const radius = (Math.sqrt(Math.max(0, 12 * share - 3)) - 3) / 6;
    return Math.max(MIN_IDEAL_SPAWN_SEPARATION, Math.floor(radius * SPAWN_SEPARATION_FACTOR));
}
function placeSpawns(hexes, coords, kingdomCount) {
    const inland = scoreCandidates(hexes, coords, true);
    // Edge hexes are a poor opening — part of the starting claim ring falls off
    // the map — so they are only considered when the map is too small to avoid them.
    const candidates = inland.length >= kingdomCount
        ? inland
        : scoreCandidates(hexes, coords, false);
    if (candidates.length < kingdomCount) {
        throw new RangeError(`Cannot place ${kingdomCount} spawns: only ${candidates.length} viable non-mountain hexes on a ${coords.length}-hex map`);
    }
    // Descending score, key as a total tie-break so the order never depends on
    // the sort implementation.
    const ranked = [...candidates].sort((a, b) => a.score === b.score ? a.key.localeCompare(b.key) : b.score - a.score);
    // Ask for the ideal separation, then back off one hex at a time. Greedy
    // best-first placement can strand itself at a spacing that is theoretically
    // achievable, and a slightly tighter map beats failing to generate one.
    // The `max(1, ...)` matters for a single-kingdom map, where the ideal is 0.
    const start = Math.max(1, idealSpawnSeparation(coords.length, kingdomCount));
    for (let separation = start; separation >= 1; separation--) {
        const chosen = greedyPlace(ranked, kingdomCount, separation);
        if (chosen.length === kingdomCount)
            return spatialOrder(chosen);
    }
    // Separation 1 can only fail if two candidates share a coordinate, which
    // rectangularMapCoords cannot produce.
    /* c8 ignore next */
    throw new Error("Unreachable: spawn placement failed at separation 1");
}
function greedyPlace(ranked, kingdomCount, separation) {
    const chosen = [];
    for (const candidate of ranked) {
        if (chosen.every((placed) => hexDistance(placed, candidate.coord) >= separation)) {
            chosen.push(candidate.coord);
            if (chosen.length === kingdomCount)
                break;
        }
    }
    return chosen;
}
function scoreCandidates(hexes, coords, requireInland) {
    const candidates = [];
    for (const coord of coords) {
        const record = hexes.get(hexKey(coord));
        if (record === undefined)
            continue;
        // Nobody starts on a peak: mountains grow no food, so the opening is a
        // starvation spiral before the player has done anything wrong.
        if (record.biome === "mountains")
            continue;
        if (requireInland && !neighbors(coord).every((n) => hexes.has(hexKey(n))))
            continue;
        const score = neighbourhoodScore(hexes, coord);
        if (score === null)
            continue;
        candidates.push({ coord, score, key: hexKey(coord) });
    }
    return candidates;
}
/**
 * Score the hexes a kingdom would actually start with — its spawn plus the
 * claim radius around it — on combined yield, penalising any resource the
 * opening cannot produce at all.
 *
 * Off-map members of the neighbourhood are skipped rather than counted as
 * barren, so the score always reads as "how good is the land that is here". The
 * penalty for being on an edge is applied once, by `requireInland`, rather than
 * smeared into the score twice. Returns null only for an off-map centre.
 */
function neighbourhoodScore(hexes, centre) {
    let totals = emptyBundle();
    let present = 0;
    for (const coord of hexSpiral(centre, STARTING_CLAIM_RADIUS)) {
        const record = hexes.get(hexKey(coord));
        if (record === undefined)
            continue;
        // `null` special on purpose: spawn quality is a judgement about terrain,
        // and specials are placed *after* spawns precisely so they can be kept away
        // from them. Feeding them in here would be reading a field that is still
        // empty and would couple the two placement passes the wrong way round.
        totals = addBundles(totals, hexYield(record.biome, record.building, null));
        present += 1;
    }
    if (present === 0)
        return null;
    // TERRAIN_RESOURCE_TYPES, not RESOURCE_TYPES: Aether is zero on every hex at
    // this point and cannot be otherwise, so including it would pin `worst` at 0
    // for every candidate and switch the scarcity term off entirely.
    let sum = 0;
    let worst = Infinity;
    for (const type of TERRAIN_RESOURCE_TYPES) {
        const ratio = ratioOfBest(totals, type, present);
        sum += ratio;
        if (ratio < worst)
            worst = ratio;
    }
    return sum / TERRAIN_RESOURCE_TYPES.length + SPAWN_SCARCITY_WEIGHT * worst;
}
/** This neighbourhood's output of one resource, as a fraction of the best terrain could give. */
function ratioOfBest(totals, type, hexCount) {
    const best = BEST_YIELD_PER_HEX[type] * hexCount;
    return best <= 0 ? 0 : totals[type] / best;
}
/**
 * Order spawns by position, not by score.
 *
 * Kingdom N is handed spawn N, so returning them best-first would silently give
 * the first kingdom — in single player, the human — the strongest opening on
 * every map. Row-major order is just as deterministic and carries no such bias.
 */
function spatialOrder(spawns) {
    return [...spawns].sort((a, b) => (a.r === b.r ? a.q - b.q : a.r - b.r));
}
//# sourceMappingURL=mapgen.js.map